/**
 * AI Chat Statistics Page
 * 
 * Trang thống kê và báo cáo lịch sử chat CVHT AI
 * Cho phép admin phân tích, đánh giá câu hỏi của sinh viên và câu trả lời của AI
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Select,
  Spin,
  message,
  Table,
  Tag,
  Input,
  Button,
  DatePicker,
  Space,
  Typography,
  Tooltip,
  Modal,
  Empty,
  Progress
} from 'antd';
import {
  MessageOutlined,
  UserOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  SearchOutlined,
  ExportOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  FireOutlined,
  ThunderboltOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as aiChatStatsService from '../../../services/aiChatStatisticsService';
import type {
  OverviewStatistics,
  QueryTrend,
  TopQuery,
  ChatLog,
  UserStatistic,
  ResponseTimeDistribution,
  HeatmapData,
  Pagination
} from '../../../services/aiChatStatisticsService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const COLORS = {
  primary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  purple: '#722ed1',
  cyan: '#13c2c2',
  gradient: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2']
};

const AIChatStatisticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');

  // Data states
  const [overview, setOverview] = useState<OverviewStatistics | null>(null);
  const [trends, setTrends] = useState<QueryTrend[]>([]);
  const [topQueries, setTopQueries] = useState<TopQuery[]>([]);
  const [chatLogs, setChatLogs] = useState<ChatLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [userStats, setUserStats] = useState<UserStatistic[]>([]);
  const [responseTimeDist, setResponseTimeDist] = useState<ResponseTimeDistribution[]>([]);
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [selectedLog, setSelectedLog] = useState<ChatLog | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    loadData();
  }, [timeRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, trendsData, topQueriesData, userStatsData, responseTimeData, heatmapResult] = await Promise.all([
        aiChatStatsService.getOverviewStatistics(timeRange),
        aiChatStatsService.getQueryTrends(timeRange, 'day'),
        aiChatStatsService.getTopQueries(timeRange, 20),
        aiChatStatsService.getUserStatistics(timeRange, 20),
        aiChatStatsService.getResponseTimeDistribution(timeRange),
        aiChatStatsService.getActivityHeatmap(timeRange)
      ]);

      setOverview(overviewData);
      setTrends(trendsData);
      setTopQueries(topQueriesData);
      setUserStats(userStatsData);
      setResponseTimeDist(responseTimeData);
      setHeatmapData(heatmapResult);
    } catch (error: any) {
      console.error('Error loading data:', error);
      message.error('Không thể tải dữ liệu thống kê');
    } finally {
      setLoading(false);
    }
  };

  const loadChatLogs = async (page: number = 1, filters: any = {}) => {
    try {
      setLoading(true);
      const result = await aiChatStatsService.getChatLogs({
        page,
        limit: 20,
        search: searchQuery,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        ...filters
      });
      setChatLogs(result.data);
      setPagination(result.pagination);
    } catch (error) {
      message.error('Không thể tải lịch sử chat');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      message.loading({ content: 'Đang xuất dữ liệu...', key: 'export' });
      await aiChatStatsService.exportChatLogs({
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        limit: 10000
      });
      message.success({ content: 'Xuất dữ liệu thành công!', key: 'export' });
    } catch (error) {
      message.error({ content: 'Không thể xuất dữ liệu', key: 'export' });
    }
  };

  const showLogDetail = (log: ChatLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  // Overview Cards
  const renderOverviewCards = () => {
    if (!overview) return null;

    const { overview: stats, today, growth } = overview;

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng số câu hỏi"
              value={stats.totalQueries}
              prefix={<MessageOutlined style={{ color: COLORS.primary }} />}
              valueStyle={{ color: COLORS.primary }}
            />
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', fontSize: 12 }}>
              {growth.queries >= 0 ? (
                <RiseOutlined style={{ color: COLORS.success, marginRight: 4 }} />
              ) : (
                <FallOutlined style={{ color: COLORS.error, marginRight: 4 }} />
              )}
              <Text type={growth.queries >= 0 ? 'success' : 'danger'}>
                {Math.abs(growth.queries)}% so với kỳ trước
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Sinh viên sử dụng"
              value={stats.uniqueUsers}
              prefix={<UserOutlined style={{ color: COLORS.purple }} />}
              valueStyle={{ color: COLORS.purple }}
            />
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', fontSize: 12 }}>
              {growth.users >= 0 ? (
                <RiseOutlined style={{ color: COLORS.success, marginRight: 4 }} />
              ) : (
                <FallOutlined style={{ color: COLORS.error, marginRight: 4 }} />
              )}
              <Text type={growth.users >= 0 ? 'success' : 'danger'}>
                {Math.abs(growth.users)}% so với kỳ trước
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Thời gian phản hồi TB"
              value={stats.avgResponseTime}
              suffix="ms"
              prefix={<ClockCircleOutlined style={{ color: COLORS.warning }} />}
              valueStyle={{ color: stats.avgResponseTime < 3000 ? COLORS.success : COLORS.warning }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Hôm nay: {today.avgResponseTime}ms
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tỷ lệ có nguồn tham chiếu"
              value={stats.groundingRate}
              suffix="%"
              prefix={<ThunderboltOutlined style={{ color: COLORS.success }} />}
              valueStyle={{ color: COLORS.success }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {stats.groundedResponses}/{stats.totalQueries} câu trả lời
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Câu hỏi hôm nay"
              value={today.queries}
              prefix={<CalendarOutlined style={{ color: COLORS.cyan }} />}
              valueStyle={{ color: COLORS.cyan }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {today.users} sinh viên
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng số phiên chat"
              value={stats.totalSessions}
              prefix={<RobotOutlined style={{ color: COLORS.primary }} />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              ~{(stats.totalQueries / stats.totalSessions || 0).toFixed(1)} câu/phiên
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Token đã sử dụng"
              value={stats.totalTokensUsed}
              prefix={<FireOutlined style={{ color: COLORS.error }} />}
              formatter={(value) => value?.toLocaleString()}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              ~{((stats.totalTokensUsed / stats.totalQueries) || 0).toFixed(0)} token/câu
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  // Query Trends Chart
  const renderTrendsChart = () => {
    if (trends.length === 0) {
      return <Empty description="Chưa có dữ liệu xu hướng" />;
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={trends}>
          <defs>
            <linearGradient id="colorQueries" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => dayjs(value).format('DD/MM')}
          />
          <YAxis />
          <RechartsTooltip
            labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
            formatter={(value: any, name: string) => {
              const labels: Record<string, string> = {
                queryCount: 'Số câu hỏi',
                userCount: 'Số người dùng',
                avgResponseTime: 'Thời gian TB (ms)'
              };
              return [value, labels[name] || name];
            }}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey="queryCount"
            name="Số câu hỏi"
            stroke={COLORS.primary}
            fillOpacity={1}
            fill="url(#colorQueries)"
          />
          <Area
            type="monotone"
            dataKey="userCount"
            name="Số người dùng"
            stroke={COLORS.success}
            fillOpacity={1}
            fill="url(#colorUsers)"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  // Top Queries Table
  const renderTopQueriesTable = () => {
    const columns: ColumnsType<TopQuery> = [
      {
        title: '#',
        dataIndex: 'rank',
        key: 'rank',
        width: 60,
        render: (rank: number) => (
          <Tag color={rank <= 3 ? 'gold' : 'default'}>
            {rank <= 3 ? '🏆' : ''} #{rank}
          </Tag>
        )
      },
      {
        title: 'Câu hỏi',
        dataIndex: 'query',
        key: 'query',
        ellipsis: true,
        render: (query: string) => (
          <Tooltip title={query}>
            <Text>{query}</Text>
          </Tooltip>
        )
      },
      {
        title: 'Số lần hỏi',
        dataIndex: 'count',
        key: 'count',
        width: 100,
        sorter: (a, b) => a.count - b.count,
        render: (count: number) => <Tag color="blue">{count}</Tag>
      },
      {
        title: 'Người hỏi',
        dataIndex: 'uniqueUsers',
        key: 'uniqueUsers',
        width: 100,
        render: (users: number) => <Text>{users} SV</Text>
      },
      {
        title: 'Thời gian TB',
        dataIndex: 'avgResponseTime',
        key: 'avgResponseTime',
        width: 120,
        render: (time: number) => (
          <Tag color={time < 3000 ? 'green' : time < 5000 ? 'orange' : 'red'}>
            {time}ms
          </Tag>
        )
      },
      {
        title: 'Lần cuối',
        dataIndex: 'lastAsked',
        key: 'lastAsked',
        width: 150,
        render: (date: string) => dayjs(date).format('DD/MM HH:mm')
      }
    ];

    return (
      <Table
        dataSource={topQueries}
        columns={columns}
        rowKey="rank"
        pagination={false}
        size="small"
      />
    );
  };

  // User Statistics Table
  const renderUserStatsTable = () => {
    const columns: ColumnsType<UserStatistic> = [
      {
        title: '#',
        dataIndex: 'rank',
        key: 'rank',
        width: 60,
        render: (rank: number) => (
          <Tag color={rank <= 3 ? 'gold' : 'default'}>#{rank}</Tag>
        )
      },
      {
        title: 'Sinh viên',
        key: 'student',
        render: (_, record) => (
          <div>
            <Text strong>{record.fullName || record.username}</Text>
            {record.studentId && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.studentId}</div>
            )}
          </div>
        )
      },
      {
        title: 'Lớp',
        dataIndex: 'className',
        key: 'className',
        width: 150,
        render: (className: string) => className || '-'
      },
      {
        title: 'Số câu hỏi',
        dataIndex: 'queryCount',
        key: 'queryCount',
        width: 100,
        sorter: (a, b) => a.queryCount - b.queryCount,
        render: (count: number) => <Tag color="blue">{count}</Tag>
      },
      {
        title: 'Số phiên',
        dataIndex: 'sessionCount',
        key: 'sessionCount',
        width: 100
      },
      {
        title: 'Hoạt động',
        key: 'activity',
        width: 180,
        render: (_, record) => (
          <div style={{ fontSize: 12 }}>
            <div>Đầu: {dayjs(record.firstQuery).format('DD/MM')}</div>
            <div>Cuối: {dayjs(record.lastQuery).format('DD/MM HH:mm')}</div>
          </div>
        )
      }
    ];

    return (
      <Table
        dataSource={userStats}
        columns={columns}
        rowKey="userId"
        pagination={false}
        size="small"
      />
    );
  };

  // Chat Logs Table
  const renderChatLogsTable = () => {
    const columns: ColumnsType<ChatLog> = [
      {
        title: 'Thời gian',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        render: (date: string) => dayjs(date).format('DD/MM HH:mm:ss')
      },
      {
        title: 'Sinh viên',
        key: 'user',
        width: 150,
        render: (_, record) => (
          <div>
            <Text>{record.user.fullName || record.user.username}</Text>
            {record.user.studentId && (
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record.user.studentId}</div>
            )}
          </div>
        )
      },
      {
        title: 'Câu hỏi',
        dataIndex: 'query',
        key: 'query',
        ellipsis: true,
        render: (query: string) => (
          <Tooltip title={query}>
            <Text>{query}</Text>
          </Tooltip>
        )
      },
      {
        title: 'Câu trả lời',
        dataIndex: 'response',
        key: 'response',
        ellipsis: true,
        width: 300,
        render: (response: string) => (
          <Tooltip title={response?.substring(0, 500)}>
            <Text type="secondary">{response?.substring(0, 100)}...</Text>
          </Tooltip>
        )
      },
      {
        title: 'Thời gian',
        dataIndex: 'responseTime',
        key: 'responseTime',
        width: 90,
        render: (time: number) => (
          <Tag color={time < 3000 ? 'green' : time < 5000 ? 'orange' : 'red'}>
            {(time / 1000).toFixed(1)}s
          </Tag>
        )
      },
      {
        title: 'Grounding',
        dataIndex: 'hasGrounding',
        key: 'hasGrounding',
        width: 90,
        render: (hasGrounding: boolean) => (
          <Tag color={hasGrounding ? 'green' : 'default'}>
            {hasGrounding ? 'Có' : 'Không'}
          </Tag>
        )
      },
      {
        title: '',
        key: 'actions',
        width: 80,
        render: (_, record) => (
          <Button type="link" size="small" onClick={() => showLogDetail(record)}>
            Chi tiết
          </Button>
        )
      }
    ];

    return (
      <>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input.Search
              placeholder="Tìm kiếm trong câu hỏi, câu trả lời..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={() => loadChatLogs(1)}
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col>
            <Button onClick={() => loadChatLogs(1)}>
              <SearchOutlined /> Tìm
            </Button>
          </Col>
          <Col>
            <Button onClick={handleExport} icon={<ExportOutlined />}>
              Xuất CSV
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={chatLogs}
          columns={columns}
          rowKey="logId"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showTotal: (total) => `Tổng ${total} bản ghi`,
            showSizeChanger: true,
            onChange: (page, pageSize) => loadChatLogs(page, { limit: pageSize })
          }}
          size="small"
        />
      </>
    );
  };

  // Response Time Distribution Chart
  const renderResponseTimeChart = () => {
    if (responseTimeDist.length === 0) {
      return <Empty description="Chưa có dữ liệu" />;
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={responseTimeDist}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timeRange" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Bar dataKey="count" name="Số lượng" fill={COLORS.primary} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Activity Heatmap (simplified as bar chart by hour)
  const renderActivityByHour = () => {
    // Group by hour
    const hourlyData = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}h`,
      value: heatmapData.filter(d => d.hour === i).reduce((sum, d) => sum + d.value, 0)
    }));

    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={hourlyData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" />
          <YAxis />
          <RechartsTooltip />
          <Bar dataKey="value" name="Số câu hỏi" fill={COLORS.cyan} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <RobotOutlined style={{ marginRight: 12 }} />
            Thống kê Chat CVHT AI
          </Title>
          <Text type="secondary">Phân tích lịch sử chat và đánh giá hiệu quả tư vấn AI</Text>
        </div>
        <Space>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 150 }}
          >
            <Option value={7}>7 ngày qua</Option>
            <Option value={30}>30 ngày qua</Option>
            <Option value={90}>90 ngày qua</Option>
            <Option value={180}>6 tháng qua</Option>
            <Option value={365}>1 năm qua</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            Làm mới
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={(key) => {
          setActiveTab(key);
          if (key === 'logs' && chatLogs.length === 0) {
            loadChatLogs(1);
          }
        }}>
          <TabPane tab={<span><QuestionCircleOutlined /> Tổng quan</span>} key="overview">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {renderOverviewCards()}

              <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                  <Card title="📈 Xu hướng sử dụng theo ngày">
                    {renderTrendsChart()}
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card title="⏱️ Phân bố thời gian phản hồi">
                    {renderResponseTimeChart()}
                  </Card>
                </Col>
              </Row>

              <Card title="🕐 Hoạt động theo giờ trong ngày">
                {renderActivityByHour()}
              </Card>
            </Space>
          </TabPane>

          <TabPane tab={<span><FireOutlined /> Câu hỏi phổ biến</span>} key="queries">
            <Card title="🏆 Top 20 câu hỏi được hỏi nhiều nhất">
              {renderTopQueriesTable()}
            </Card>
          </TabPane>

          <TabPane tab={<span><UserOutlined /> Người dùng</span>} key="users">
            <Card title="👤 Top sinh viên sử dụng nhiều nhất">
              {renderUserStatsTable()}
            </Card>
          </TabPane>

          <TabPane tab={<span><MessageOutlined /> Lịch sử chat</span>} key="logs">
            <Card title="📜 Chi tiết lịch sử chat">
              {renderChatLogsTable()}
            </Card>
          </TabPane>
        </Tabs>
      </Spin>

      {/* Detail Modal */}
      <Modal
        title="Chi tiết câu hỏi & trả lời"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Thời gian:</Text>
                <div>{dayjs(selectedLog.createdAt).format('DD/MM/YYYY HH:mm:ss')}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Sinh viên:</Text>
                <div>
                  {selectedLog.user.fullName || selectedLog.user.username}
                  {selectedLog.user.studentId && ` (${selectedLog.user.studentId})`}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Thời gian phản hồi:</Text>
                <div>
                  <Tag color={selectedLog.responseTime < 3000 ? 'green' : 'orange'}>
                    {(selectedLog.responseTime / 1000).toFixed(2)}s
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Có nguồn tham chiếu:</Text>
                <div>
                  <Tag color={selectedLog.hasGrounding ? 'green' : 'default'}>
                    {selectedLog.hasGrounding ? 'Có' : 'Không'}
                  </Tag>
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Câu hỏi:</Text>
              <Card size="small" style={{ marginTop: 8, background: '#f6ffed' }}>
                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {selectedLog.query}
                </Paragraph>
              </Card>
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Câu trả lời của AI:</Text>
              <Card size="small" style={{ marginTop: 8, background: '#e6f7ff', maxHeight: 400, overflow: 'auto' }}>
                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {selectedLog.response}
                </Paragraph>
              </Card>
            </div>

            {selectedLog.sourceDocuments && selectedLog.sourceDocuments.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Nguồn tài liệu:</Text>
                <div style={{ marginTop: 8 }}>
                  {selectedLog.sourceDocuments.map((doc: any, index: number) => (
                    <Tag key={index} color="blue">{doc.documentTitle || doc.fileName || 'Unknown'}</Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AIChatStatisticsPage;

